
import { Controller } from '@hotwired/stimulus'

const PREVIEW_DEBOUNCE_MS = 300
const SUBMIT_GUARD_MS = 5000

export default class extends Controller {
  static targets = [
    'query',
    'kind',
    'preview',
    'previewName',
    'previewMeta',
    'previewMother',
    'previewMotherRow',
    'previewUnderAge',
    'previewUnavailable',
    'previewLoading',
    'previewDelayed',
    'previewStatus',
    'submitButton'
  ]

  connect() {
    this.format()
    this.setSubmitEnabled(false)
    this.preview()
  }

  disconnect() {
    clearTimeout(this.previewTimeout)
    clearTimeout(this.submitGuardTimeout)
    this.previewRequest?.abort()
  }

  format() {
    if (this.kindTarget.value === 'cpf') {
      this.formatCpf()
    } else if (this.kindTarget.value === 'phone_number') {
      this.formatPhoneNumber()
    }
  }

  preview() {
    const digits = this.queryTarget.value.replace(/\D/g, '')

    clearTimeout(this.previewTimeout)
    clearTimeout(this.submitGuardTimeout)
    this.previewRequest?.abort()
    this.previewRequest = null
    this.hidePreviewFeedback()
    this.setSubmitEnabled(false)

    if (digits.length !== 11) return

    this.previewTimeout = setTimeout(() => this.fetchPreview(digits), PREVIEW_DEBOUNCE_MS)
    // The preview is a nicety, so a slow or hanging lookup must never keep the button disabled.
    // The server still vets the CPF on submit, so releasing early cannot slip anything through.
    this.submitGuardTimeout = setTimeout(() => {
      if (this.previewRequest && this.currentCpf() === digits) {
        this.showPreviewDelayed()
        this.setSubmitEnabled(true)
      }
    }, PREVIEW_DEBOUNCE_MS + SUBMIT_GUARD_MS)
  }

  async fetchPreview(cpf) {
    const request = new AbortController()
    this.previewRequest = request
    this.showPreviewLoading()

    try {
      const response = await fetch(`/anonymous_searches/preview?cpf=${cpf}`, { signal: request.signal })
      if (!response.ok) throw new Error('Preview request failed')

      const data = await response.json()
      if (this.currentCpf() !== cpf || this.previewRequest !== request) return

      this.renderPreview(data)
    } catch (error) {
      if (error.name === 'AbortError' || this.currentCpf() !== cpf || this.previewRequest !== request) return

      // Preview is best effort; never block the search when it fails.
      this.setSubmitEnabled(true)
    } finally {
      if (this.previewRequest === request) {
        clearTimeout(this.submitGuardTimeout)
        this.hidePreviewProgress()
        this.previewRequest = null
      }
    }
  }

  renderPreview(data) {
    this.hidePreviewFeedback()

    if (data.skipped) {
      this.setSubmitEnabled(true)
      return
    }

    if (data.censored) {
      if (this.hasPreviewUnderAgeTarget) this.previewUnderAgeTarget.classList.remove('hidden')
      this.setSubmitEnabled(false)
      return
    }

    if (data.found !== true) {
      this.showPreviewUnavailable()
      return
    }

    this.setSubmitEnabled(true)
    if (!this.hasPreviewTarget) return

    this.previewNameTarget.textContent = data.name || ''

    const metaParts = []
    if (data.age) metaParts.push(`${data.age} anos`)
    if (data.gender === 'male') metaParts.push('Masculino')
    else if (data.gender === 'female') metaParts.push('Feminino')
    this.previewMetaTarget.textContent = metaParts.join(' · ')

    if (this.hasPreviewMotherTarget) {
      this.previewMotherTarget.textContent = data.mother_name || ''
    }
    if (this.hasPreviewMotherRowTarget) {
      this.previewMotherRowTarget.classList.toggle('hidden', !data.mother_name)
    }

    this.previewTarget.classList.remove('hidden')
  }

  showPreviewUnavailable() {
    this.hidePreviewFeedback()
    if (this.hasPreviewUnavailableTarget) this.previewUnavailableTarget.classList.remove('hidden')
    this.setSubmitEnabled(false)
  }

  hidePreviewFeedback() {
    this.hidePreviewProgress()
    if (this.hasPreviewTarget) this.previewTarget.classList.add('hidden')
    if (this.hasPreviewUnderAgeTarget) this.previewUnderAgeTarget.classList.add('hidden')
    if (this.hasPreviewUnavailableTarget) this.previewUnavailableTarget.classList.add('hidden')
  }

  showPreviewLoading() {
    if (this.hasPreviewLoadingTarget) this.previewLoadingTarget.classList.remove('hidden')
    if (this.hasPreviewStatusTarget) this.previewStatusTarget.textContent = 'Validando CPF…'
  }

  showPreviewDelayed() {
    this.hidePreviewProgress()
    if (this.hasPreviewDelayedTarget) this.previewDelayedTarget.classList.remove('hidden')
    if (this.hasPreviewStatusTarget) this.previewStatusTarget.textContent = 'A prévia está demorando. Você pode continuar.'
  }

  hidePreviewProgress() {
    if (this.hasPreviewLoadingTarget) this.previewLoadingTarget.classList.add('hidden')
    if (this.hasPreviewDelayedTarget) this.previewDelayedTarget.classList.add('hidden')
    if (this.hasPreviewStatusTarget) this.previewStatusTarget.textContent = ''
  }

  setSubmitEnabled(enabled) {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = !enabled
  }

  currentCpf() {
    return this.queryTarget.value.replace(/\D/g, '')
  }

  formatCpf() {
    let value = this.queryTarget.value.replace(/\D/g, "")
    value = value.replace(/(\d{3})(\d)/, "$1.$2")
    value = value.replace(/(\d{3})(\d)/, "$1.$2")
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    this.queryTarget.value = value
  }

  formatPhoneNumber() {
    let value = this.queryTarget.value.replace(/\D/g, "")
    value = value.replace(/(\d{2})(\d)/, "($1) $2")
    value = value.replace(/(\d{4,5})(\d{4})$/, "$1-$2")
    this.queryTarget.value = value
  }
}
